import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Trash2, X, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { DateDisplay } from '@/components/date-display';
import { useAccountTimezone } from '@/hooks/use-account-timezone';
import { toPromotionDayString, storedPromotionDayString } from '@/lib/promotion-date';
import { formatMoney, getCurrencyInfo } from '@/lib/money';
import type { PromotionCodeItem } from '@/lib/api/promotionCodes';

export interface PromotionCodeFormData {
  code: string;
  active: boolean;
  usageLimit?: number;
  expiresAt?: string;
  emailPattern?: string;
  firstTimeCustomersOnly: boolean;
}

interface EditablePromotionCodeRowProps {
  promotionCode?: PromotionCodeItem;
  isEditing: boolean;
  isSaving: boolean;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (data: PromotionCodeFormData) => Promise<void>;
  onDelete: () => void;
}

// Unknown codes would make formatMoney throw, so fall back to a plain amount.
function formatRevenue(amount: number, currencyCode: string) {
  return getCurrencyInfo(currencyCode)
    ? formatMoney(amount, currencyCode)
    : `${(amount / 100).toFixed(2)} ${currencyCode}`;
}

function getInitialState(promotionCode: PromotionCodeItem | undefined, timezone: string): PromotionCodeFormData {
  return {
    code: promotionCode?.code ?? '',
    active: promotionCode?.active ?? true,
    usageLimit: promotionCode?.usageLimit ?? undefined,
    expiresAt: storedPromotionDayString(promotionCode?.expiresAt, timezone),
    emailPattern: promotionCode?.emailPattern ?? undefined,
    firstTimeCustomersOnly: promotionCode?.firstTimeCustomersOnly ?? false,
  };
}

export const EditablePromotionCodeRow = ({
  promotionCode,
  isEditing,
  isSaving,
  selected,
  onSelectChange,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: EditablePromotionCodeRowProps) => {
  const { t } = useTranslation('stores');
  const { timezone } = useAccountTimezone();
  const [form, setForm] = React.useState<PromotionCodeFormData>(() =>
    getInitialState(promotionCode, timezone),
  );
  const [error, setError] = React.useState<string | null>(null);

  // Revenue is derived from paid orders — read-only in both modes.
  const revenue = promotionCode?.revenue ?? [];
  const revenueCell =
    revenue.length === 0 ? (
      <span className="text-muted-foreground">—</span>
    ) : (
      <div className="flex flex-col">
        {revenue.map((entry) => (
          <span key={entry.currency}>{formatRevenue(entry.amount, entry.currency)}</span>
        ))}
      </div>
    );

  const updateField = <K extends keyof PromotionCodeFormData>(
    key: K,
    value: PromotionCodeFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      setError(
        t('promotionCodes.validation.codeRequired'),
      );
      return;
    }
    if (
      form.usageLimit !== undefined &&
      (form.usageLimit <= 0 || !Number.isInteger(form.usageLimit))
    ) {
      setError(
        t('promotionCodes.validation.usageLimitInvalid'),
      );
      return;
    }
    setError(null);
    await onSave({
      ...form,
      code: form.code.trim(),
      emailPattern: form.emailPattern?.trim() || undefined,
    });
  };

  const handleCancel = () => {
    setForm(getInitialState(promotionCode, timezone));
    setError(null);
    onCancel();
  };

  if (!isEditing) {
    const usageCount = promotionCode?.usageCount ?? 0;
    const usageLimit = promotionCode?.usageLimit;
    return (
      <TableRow>
        <TableCell className="w-10">
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => onSelectChange(!!checked)}
          />
        </TableCell>
        <TableCell className="font-mono font-medium">{promotionCode?.code}</TableCell>
        <TableCell>
          <Badge variant={promotionCode?.active ? 'default' : 'secondary'}>
            {promotionCode?.active
              ? t('promotionCodes.status.active')
              : t('promotionCodes.status.inactive')}
          </Badge>
        </TableCell>
        <TableCell className="text-sm">
          {usageLimit ? (
            <span>
              {usageCount} / {usageLimit}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {usageCount}{' '}
              <span className="text-xs">
                ({t('promotionCodes.unlimited')})
              </span>
            </span>
          )}
        </TableCell>
        <TableCell className="text-sm font-medium">{revenueCell}</TableCell>
        <TableCell className="text-sm">
          <DateDisplay
            value={promotionCode?.expiresAt}
            mode="date"
            fallback={
              <span className="text-muted-foreground">
                {t('promotionCodes.noExpiration')}
              </span>
            }
          />
        </TableCell>
        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
          {promotionCode?.emailPattern || '—'}
        </TableCell>
        <TableCell>
          {promotionCode?.firstTimeCustomersOnly && (
            <Badge variant="outline">
              {t('promotionCodes.columns.firstTimeOnly')}
            </Badge>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className="bg-muted/30">
      <TableCell className="w-10">
        <Checkbox checked={selected} onCheckedChange={(checked) => onSelectChange(!!checked)} disabled />
      </TableCell>
      <TableCell>
        <Input
          value={form.code}
          onChange={(e) => updateField('code', e.target.value)}
          placeholder={t('promotionCodes.placeholders.code')}
          className="h-8 w-full min-w-[120px] font-mono"
          autoFocus={!promotionCode}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={form.active}
          onCheckedChange={(checked) => updateField('active', checked)}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min="1"
          step="1"
          value={form.usageLimit ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            updateField('usageLimit', v === '' ? undefined : parseInt(v, 10));
          }}
          placeholder="∞"
          className="h-8 w-20"
        />
      </TableCell>
      <TableCell className="text-sm">{revenueCell}</TableCell>
      <TableCell>
        <DatePicker
          value={form.expiresAt ? new Date(`${form.expiresAt}T00:00:00`) : null}
          onChange={(date) =>
            updateField('expiresAt', date ? toPromotionDayString(date) : undefined)
          }
          placeholder={t('promotionCodes.noExpiration')}
        />
      </TableCell>
      <TableCell>
        <Input
          value={form.emailPattern ?? ''}
          onChange={(e) => updateField('emailPattern', e.target.value)}
          placeholder={t('promotionCodes.placeholders.email')}
          className="h-8 w-full min-w-[160px]"
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={form.firstTimeCustomersOnly}
          onCheckedChange={(checked) => updateField('firstTimeCustomersOnly', checked)}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {error && (
            <span className="mr-1 text-xs text-destructive" title={error}>
              !
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-green-600"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};
